"use client";

import { useEffect, useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { ApiError, api, loadTokens, type MeProfile } from "@/lib/api";
import { loginUrl } from "@/lib/loginUrl";
import { SiteHeader } from "@/components/SiteHeader";
import { ClientLocaleDate } from "@/components/ClientLocaleDate";
import { SiteSidebar } from "@/components/SiteSidebar";
import { Skeleton } from "@/components/Skeleton";
import { EmailChangeForm } from "@/components/EmailChangeForm";

/**
 * STORY-107 — settings/security page.
 *
 * Renders one of three states:
 *
 *  1. **Disabled.** "Set up 2FA" button → calls /v1/me/2fa/setup,
 *     transitions to Enrolling state with the secret + otpauthUrl.
 *  2. **Enrolling.** Shows the manual entry key + the otpauth URL
 *     (admin scans / pastes into authenticator app), then a 6-digit
 *     code field → calls /v1/me/2fa/verify-and-enable on submit.
 *  3. **Enabled.** Shows enabled-since date and a code input to
 *     disable. /v1/me/2fa/disable requires the current TOTP, so a
 *     stolen access token cannot disable 2FA on its own.
 *
 * Closed-beta scope: no QR code rendering — admin pastes the
 * `otpauthUrl` into Google Authenticator's "Enter setup key"
 * flow OR types the manual base32 secret. QR rendering is a
 * Phase 2 follow-up (needs a JS QR lib; not worth the dep
 * tonight).
 */
export default function SecuritySettingsPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const [profile, setProfile] = useState<MeProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Enrolling state — populated after /setup, cleared on success.
  const [setupSecret, setSetupSecret] = useState<string | null>(null);
  const [setupUrl, setSetupUrl] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState("");

  // Disable form
  const [disableCode, setDisableCode] = useState("");

  useEffect(() => {
    if (!loadTokens()) {
      router.replace(loginUrl());
      return;
    }
    void api
      .me()
      .then((p) => setProfile(p))
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          router.replace(loginUrl());
        } else {
          setError(
            err instanceof Error ? err.message : t("securityPage.errLoad"),
          );
        }
      });
  }, [router]);

  async function handleStartSetup() {
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.startTwoFaSetup();
      setSetupSecret(result.secret);
      setSetupUrl(result.otpauthUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("securityPage.errSetup"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerifyAndEnable(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(verifyCode)) {
      setError(t("securityPage.errCodeFormat"));
      return;
    }
    setSubmitting(true);
    try {
      await api.verifyAndEnableTwoFa(verifyCode);
      // Refresh profile so the page flips to Enabled state.
      const p = await api.me();
      setProfile(p);
      setSetupSecret(null);
      setSetupUrl(null);
      setVerifyCode("");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("securityPage.errVerify"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDisable(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(disableCode)) {
      setError("Code must be 6 digits.");
      return;
    }
    setSubmitting(true);
    try {
      await api.disableTwoFa(disableCode);
      const p = await api.me();
      setProfile(p);
      setDisableCode("");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("securityPage.errDisable"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!profile) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto max-w-[800px] p-8">
          <div
            role="status"
            aria-label={t("securityPage.loadingAria")}
            className="space-y-3"
          >
            <Skeleton className="h-7 w-44" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-32 w-full rounded-md" />
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="min-w-0 max-w-2xl">
          <h1 className="mb-2 text-[22px] font-bold tracking-tight">
            {t("securityPage.title")}
          </h1>
          <p className="mb-6 text-[13px] text-[var(--color-text3)]">
            {t("securityPage.subtitle")}
          </p>

          {error && (
            <div
              role="alert"
              className="mb-4 rounded-md border px-3 py-2 text-sm"
              style={{
                borderColor:
                  "color-mix(in srgb, var(--color-status-negative) 50%, transparent)",
                background:
                  "color-mix(in srgb, var(--color-status-negative) 8%, transparent)",
                color: "var(--color-status-negative)",
              }}
            >
              {error}
            </div>
          )}

          {!profile.two_fa_enabled && !setupSecret && (
            <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
              <h2 className="text-[14px] font-bold">
                {t("securityPage.disabledHeading")}
              </h2>
              <p className="mt-1 text-[12px] text-[var(--color-text3)]">
                {t("securityPage.disabledBody")}
              </p>
              <button
                type="button"
                onClick={handleStartSetup}
                disabled={submitting}
                className="mt-3 inline-flex h-10 items-center justify-center rounded-md px-4 text-[13px] font-semibold text-white disabled:opacity-60"
                style={{ background: "var(--color-brand)" }}
              >
                {submitting
                  ? t("securityPage.starting")
                  : t("securityPage.setupCta")}
              </button>
            </section>
          )}

          {!profile.two_fa_enabled && setupSecret && setupUrl && (
            <section className="rounded-md border border-[var(--color-brand)] bg-[var(--color-bg)] p-4">
              <h2 className="text-[14px] font-bold">
                {t("securityPage.enrollingHeading")}
              </h2>
              <p className="mt-1 text-[12px] text-[var(--color-text3)]">
                {t("securityPage.enrollingBody")}
              </p>

              <label className="mt-4 block text-[11px] font-semibold uppercase tracking-[0.4px] text-[var(--color-text3)]">
                {t("securityPage.manualKeyLabel")}
              </label>
              <code className="mt-1 block break-all rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-2 font-mono text-[12px]">
                {setupSecret}
              </code>

              <label className="mt-4 block text-[11px] font-semibold uppercase tracking-[0.4px] text-[var(--color-text3)]">
                {t("securityPage.otpauthUrlLabel")}
              </label>
              <code className="mt-1 block break-all rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-2 font-mono text-[10px]">
                {setupUrl}
              </code>

              <form
                onSubmit={handleVerifyAndEnable}
                className="mt-4 flex flex-col gap-2"
              >
                <label className="text-[11px] font-semibold uppercase tracking-[0.4px] text-[var(--color-text3)]">
                  {t("securityPage.verifyCodeLabel")}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) =>
                    setVerifyCode(e.target.value.replace(/\D/g, ""))
                  }
                  autoComplete="one-time-code"
                  required
                  className="h-10 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 font-mono text-[14px] tracking-[0.4em] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex h-10 items-center justify-center rounded-md px-4 text-[13px] font-semibold text-white disabled:opacity-60"
                    style={{ background: "var(--color-brand)" }}
                  >
                    {submitting
                      ? t("securityPage.verifying")
                      : t("securityPage.verifyAndEnable")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSetupSecret(null);
                      setSetupUrl(null);
                      setVerifyCode("");
                      setError(null);
                    }}
                    className="inline-flex h-10 items-center justify-center rounded-md border border-[var(--color-border)] px-4 text-[12px] text-[var(--color-text2)] hover:text-[var(--color-text)]"
                  >
                    {t("securityPage.cancel")}
                  </button>
                </div>
              </form>
            </section>
          )}

          {profile.two_fa_enabled && (
            <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
              <h2 className="text-[14px] font-bold">
                {t("securityPage.enabledHeading")}
              </h2>
              <p className="mt-1 text-[12px] text-[var(--color-text3)]">
                {profile.two_fa_enabled_at ? (
                  <>
                    {t("securityPage.enabledSince", { when: "" }).replace(
                      /\s+$/,
                      "",
                    )}{" "}
                    <ClientLocaleDate
                      iso={profile.two_fa_enabled_at}
                      kind="datetime"
                    />
                  </>
                ) : (
                  t("securityPage.enabledSince", { when: "" })
                )}
              </p>

              <form
                onSubmit={handleDisable}
                className="mt-4 flex flex-col gap-2"
              >
                <label className="text-[11px] font-semibold uppercase tracking-[0.4px] text-[var(--color-text3)]">
                  {t("securityPage.currentCodeLabel")}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={disableCode}
                  onChange={(e) =>
                    setDisableCode(e.target.value.replace(/\D/g, ""))
                  }
                  autoComplete="one-time-code"
                  required
                  className="h-10 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 font-mono text-[14px] tracking-[0.4em] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex h-10 items-center justify-center self-start rounded-md border px-4 text-[13px] font-semibold disabled:opacity-60"
                  style={{
                    borderColor:
                      "color-mix(in srgb, var(--color-status-negative) 50%, transparent)",
                    background:
                      "color-mix(in srgb, var(--color-status-negative) 8%, transparent)",
                    color: "var(--color-status-negative)",
                  }}
                >
                  {submitting
                    ? t("securityPage.disabling")
                    : t("securityPage.disableCta")}
                </button>
              </form>
            </section>
          )}

          {/* STORY-377 — email change. Sits below 2FA so the heaviest
              security control comes first; email change is a soft
              identity action that benefits from the audit trail
              already carried by users.email + the email_changes
              table. Component handles its own state. */}
          <section className="mt-4 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
            <h2 className="text-[14px] font-bold">
              {t("securityPage.emailHeading")}
            </h2>
            <p className="mt-1 mb-3 text-[12px] text-[var(--color-text3)]">
              {t("securityPage.currentEmailLabel")}{" "}
              <span className="font-mono text-[var(--color-text2)]">
                {profile.email}
              </span>
            </p>
            <EmailChangeForm />
          </section>
        </main>
      </div>
    </>
  );
}
